import type { Calibration } from './calibration';
import { createHoldDetector } from './holdDetector';
import { laughScore } from './scoring';
import type { FaceFrame } from './types';

export interface LaughEvent {
  /** Début du rire (horloge locale, ms) : sert à l'arbitrage « qui a ri en premier ». */
  readonly t: number;
  /** Score maximal atteint avant confirmation. */
  readonly score: number;
}

export interface LaughDetectorOptions {
  readonly threshold: number;
  readonly release: number;
  /** Durée continue au-dessus du seuil avant de confirmer un rire. */
  readonly minDurationMs: number;
  /** Trou maximal sans visage toléré au sein d'un rire. */
  readonly maxGapMs: number;
}

export const DEFAULT_LAUGH_TIMING = { minDurationMs: 300, maxGapMs: 200 } as const;

export interface LaughDetector {
  /** Nouvel échantillon. `score === null` : pas de visage sur cette frame. */
  push(t: number, score: number | null): LaughEvent | null;
  reset(): void;
}

/**
 * Flux de scores horodatés → événements « rire ».
 * Un rire est confirmé quand le score reste au-dessus de `threshold` (puis de
 * `release`, hystérésis) pendant `minDurationMs`. Un seul événement par rire.
 */
export function createLaughDetector(options: LaughDetectorOptions): LaughDetector {
  const hold = createHoldDetector({
    on: options.threshold,
    off: options.release,
    minDurationMs: options.minDurationMs,
    maxGapMs: options.maxGapMs,
  });
  return {
    push(t, score) {
      const e = hold.push(t, score);
      return e && { t: e.t, score: e.peak };
    },
    reset: () => {
      hold.reset();
    },
  };
}

/** Utilitaire (tests, outil dev) : fait passer une séquence de frames dans un détecteur calibré. */
export function detectLaughs(
  frames: readonly FaceFrame[],
  calibration: Calibration,
  timing: Pick<LaughDetectorOptions, 'minDurationMs' | 'maxGapMs'> = DEFAULT_LAUGH_TIMING,
): LaughEvent[] {
  const detector = createLaughDetector({
    threshold: calibration.threshold,
    release: calibration.release,
    ...timing,
  });
  return frames.flatMap(
    (f) => detector.push(f.t, f.blendshapes ? laughScore(f.blendshapes) : null) ?? [],
  );
}
